import './style.scss';

/**
 * Cover Parallax Effect — frontend script (Pro)
 * Improved with accessibility and performance optimizations
 */

// Check if user prefers reduced motion (accessibility)
const prefersReducedMotion = window.matchMedia(
	'(prefers-reduced-motion: reduce)'
).matches;

// Store all active parallax elements
const parallaxElements = new Set();
let resizeTimer;

const updateParallax = (element) => {
	// Skip if user prefers reduced motion
	if (prefersReducedMotion) {
		return;
	}

	const background = element.querySelector(
		'.wp-block-cover__image-background, .wp-block-cover__video-background'
	);

	if (!background) {
		return;
	}

	const speed = parseFloat(element.dataset.parallaxSpeed) || 0.5;
	const rect = element.getBoundingClientRect();
	const windowHeight = window.innerHeight;

	// Skip update if element is not visible
	if (rect.top >= windowHeight || rect.bottom <= 0) {
		// Reset position when not visible
		background.style.transform = 'translate3d(0, 0, 0)';
		return;
	}

	// Calculate parallax offset with proper normalization
	const scrollProgress =
		(windowHeight - rect.top) / (windowHeight + rect.height);
	// Clamp between 0 and 1 for better control
	const normalizedProgress = Math.max(0, Math.min(1, scrollProgress));
	const yPos = (normalizedProgress - 0.5) * speed * 20;

	background.style.transform = `translate3d(0, ${yPos}%, 0)`;
};

const updateAllParallax = () => {
	parallaxElements.forEach((element) => {
		if (element.isConnected) {
			updateParallax(element);
		} else {
			parallaxElements.delete(element);
		}
	});
};

const initParallax = (element) => {
	if (!element || element.dataset.parallaxInit === '1') {
		return;
	}

	element.dataset.parallaxInit = '1';
	parallaxElements.add(element);
	updateParallax(element);
};

const initAll = (root = document) => {
	root.querySelectorAll('.wp-block-cover.mnorwise-scroll-parallax').forEach(
		initParallax
	);
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => initAll(document));
} else {
	initAll(document);
}

// Single global scroll/resize listener for all parallax elements
window.addEventListener('scroll', updateAllParallax, { passive: true });
window.addEventListener(
	'resize',
	() => {
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(updateAllParallax, 150);
	},
	{ passive: true }
);

const observer = new MutationObserver((mutations) => {
	mutations.forEach((mutation) => {
		mutation.addedNodes.forEach((node) => {
			if (!node || node.nodeType !== 1) {
				return;
			}
			if (node.matches?.('.wp-block-cover.mnorwise-scroll-parallax')) {
				initParallax(node);
			}
			if (node.querySelectorAll) {
				node.querySelectorAll(
					'.wp-block-cover.mnorwise-scroll-parallax'
				).forEach(initParallax);
			}
		});
	});
});

observer.observe(document.body, { childList: true, subtree: true });

// Listen for changes to prefers-reduced-motion preference
window
	.matchMedia('(prefers-reduced-motion: reduce)')
	.addEventListener('change', (e) => {
		if (e.matches) {
			// Reset all parallax effects when user enables reduced motion
			parallaxElements.forEach((element) => {
				const background = element.querySelector(
					'.wp-block-cover__image-background, .wp-block-cover__video-background'
				);
				if (background) {
					background.style.transform = 'translate3d(0, 0, 0)';
				}
			});
		}
	});
